import { Table } from "@gladis/ui";
import { useT } from "@gladis/ui/i18n";
import type { ReactNode } from "react";

import type { PositionRowDto } from "../dto.js";
import { formatGain, formatMoney, formatPercent, formatPrice, formatShares } from "../format.js";
import { StockActions } from "./StockActions.js";

/**
 * One row per holding.
 *
 * ==========================================================================================
 * "NO PRICE" IS RENDERED AS TEXT, NEVER AS ZERO
 * ==========================================================================================
 *
 * The wire sends `null` for a holding with no price, and the temptation at every one of these call
 * sites is `?? 0`. That would report a stock the household owns as worth nothing — the defect the
 * whole unpriced rule exists to prevent — and it would look completely ordinary in review.
 *
 * The server has already sorted unpriced positions LAST, so they do not sit at the top of a table
 * ordered by value as though they were the smallest holdings.
 *
 * A STOCK WITH NO TRANSACTIONS shows em-dashes and belongs here rather than among the closed
 * positions. Same zero holding, opposite meaning about the past.
 *
 * THE NAME IS A BUTTON, NOT THE ROW. A whole clickable `<tr>` is unreachable by keyboard and
 * announces nothing useful to a screen reader; a real control in the first cell is both.
 *
 * ==========================================================================================
 * A CLOSED POSITION IS A DIFFERENT TABLE, NOT THE SAME ONE WITH BLANKS
 * ==========================================================================================
 *
 * It has nothing to say about its current state: no shares, no average cost — there is nothing left
 * to average — and no value. Rendering "0" and an em-dash under those headings is true and useless.
 *
 * What it has is history: this many shares went out, they had cost this, they fetched that, and the
 * difference is the realised gain. So the closed table asks different questions of the same row.
 *
 * EVERY ROW CARRIES THE SAME ACTIONS THE DETAIL VIEW DOES, behind one menu — recording a purchase or
 * a price is the common case, and making somebody open a stock to do it turns two clicks into four.
 * `StockActions` is the single implementation, so the list and the detail view cannot drift on which
 * actions exist or when Remove is offered.
 */

export interface PositionsTableProps {
  positions: readonly PositionRowDto[];
  /** Closed positions show realised gain where active ones show unrealised. */
  variant?: "active" | "closed";
  onOpen: (stockId: string) => void;
}

export function PositionsTable({
  positions,
  variant = "active",
  onOpen,
}: PositionsTableProps): ReactNode {
  const t = useT();
  const closed = variant === "closed";

  return (
    <Table>
      <Table.Head>
        <Table.Row>
          <Table.HeaderCell>{t("inv.table.stock")}</Table.HeaderCell>
          {closed ? (
            <>
              <Table.HeaderCell numeric>{t("inv.table.sharesSold")}</Table.HeaderCell>
              <Table.HeaderCell numeric>{t("inv.table.cost")}</Table.HeaderCell>
              <Table.HeaderCell numeric>{t("inv.table.proceeds")}</Table.HeaderCell>
              <Table.HeaderCell numeric>{t("inv.table.realised")}</Table.HeaderCell>
            </>
          ) : (
            <>
              <Table.HeaderCell numeric>{t("inv.table.shares")}</Table.HeaderCell>
              <Table.HeaderCell numeric>{t("inv.table.averageCost")}</Table.HeaderCell>
              <Table.HeaderCell numeric>{t("inv.table.price")}</Table.HeaderCell>
              <Table.HeaderCell numeric>{t("inv.table.value")}</Table.HeaderCell>
              <Table.HeaderCell numeric>{t("inv.table.unrealised")}</Table.HeaderCell>
            </>
          )}
          <Table.HeaderCell>
            <span className="sr-only">{t("inv.table.actions")}</span>
          </Table.HeaderCell>
        </Table.Row>
      </Table.Head>
      <Table.Body>
        {positions.map((row) => {
          const unpriced = row.currentValue === null;
          return (
            <Table.Row key={row.stockId}>
              <Table.Cell>
                <button
                  type="button"
                  className="text-left hover:underline"
                  onClick={() => {
                    onOpen(row.stockId);
                  }}
                >
                  <span className="font-medium">{row.name}</span>
                  <span className="text-muted ml-2 text-xs">{row.symbol}</span>
                </button>
              </Table.Cell>
              {closed ? (
                <>
                  <Table.Cell numeric>{formatShares(row.sharesSold)}</Table.Cell>
                  <Table.Cell numeric>{formatMoney(row.costOfSold)}</Table.Cell>
                  <Table.Cell numeric>{formatMoney(row.proceeds)}</Table.Cell>
                  <Table.Cell numeric>{formatGain(row.realisedGain)}</Table.Cell>
                </>
              ) : (
                <>
                  <Table.Cell numeric>
                    {row.hasTransactions ? formatShares(row.sharesHeld) : "—"}
                  </Table.Cell>
                  <Table.Cell numeric>{formatMoney(row.averageCost)}</Table.Cell>
                  <Table.Cell numeric>
                    {unpriced ? (
                      <span className="text-muted">{t("inv.table.noPrice")}</span>
                    ) : (
                      formatPrice(row.currentPrice)
                    )}
                  </Table.Cell>
                  <Table.Cell numeric>
                    {unpriced ? (
                      <span className="text-muted">{t("inv.table.noPrice")}</span>
                    ) : (
                      formatMoney(row.currentValue)
                    )}
                  </Table.Cell>
                  <Table.Cell numeric>
                    {unpriced ? (
                      <span className="text-muted">—</span>
                    ) : (
                      <>
                        {formatGain(row.unrealisedGain)}
                        <span className="text-muted ml-2 text-xs">
                          {formatPercent(row.unrealisedGainPercent)}
                        </span>
                      </>
                    )}
                  </Table.Cell>
                </>
              )}
              <Table.Cell>
                <StockActions
                  stockId={row.stockId}
                  name={row.name}
                  symbol={row.symbol}
                  exchange={null}
                  canBeRemoved={row.canBeRemoved}
                  label={`Actions for ${row.symbol}`}
                />
              </Table.Cell>
            </Table.Row>
          );
        })}
      </Table.Body>
    </Table>
  );
}
